import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Hash } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layout } from '@/components/Layout';
import { VideoGrid } from '@/components/VideoGrid';
import { FeedFilterDropdown } from '@/components/FeedFilterDropdown';
import { useVideoStream } from '@/hooks/useStreamingVideos';
import { useReportsStream } from '@/hooks/useReports';
import { useVideoFeedFilter } from '@/hooks/useVideoFeedFilter';
import { isVideoReported } from '@/lib/reportUtils';
import { isYouTubeUrl } from '@/lib/videoTypes';
import NotFound from './NotFound';

export default function HashtagPage() {
  const { tag } = useParams<{ tag: string }>();
  const normalized = (tag ?? '').toLowerCase();

  const stream = useVideoStream({ tag: normalized || undefined, enabled: !!normalized });
  const { filter } = useVideoFeedFilter();
  const reports = useReportsStream(!!normalized);

  const filtered = useMemo(() => {
    return stream.videos.filter((video) => {
      // Ensure the hashtag is actually present (relay-level #t filter is
      // authoritative, but be strict about case variants)
      if (!video.hashtags.some(t => t.toLowerCase() === normalized)) return false;
      if (!filter.showYouTube && isYouTubeUrl(video.videoUrl)) return false;
      if (!filter.showUnknownDuration && video.duration === undefined) return false;
      if (!filter.showReported && isVideoReported(video, reports)) return false;
      return true;
    });
  }, [stream.videos, filter, reports, normalized]);

  if (!normalized) {
    return <NotFound />;
  }

  const horizontal = filtered.filter(v => !v.isVertical);
  const vertical = filtered.filter(v => v.isVertical);

  return (
    <Layout>
      <div className="mx-auto max-w-[2000px] px-3 sm:px-6 py-5">
        <Tabs defaultValue="videos">
          {/* Two rows on mobile so a long tag can't overlap the filter
              button; single row from sm: up. */}
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="flex items-center gap-2 text-lg font-semibold min-w-0">
              <Hash className="h-5 w-5 shrink-0" />
              <span className="truncate">{normalized}</span>
            </h1>
            <div className="flex items-center justify-between gap-3 shrink-0">
              <TabsList>
                <TabsTrigger value="videos">Videos</TabsTrigger>
                <TabsTrigger value="shorts">Shorts</TabsTrigger>
              </TabsList>
              <FeedFilterDropdown />
            </div>
          </div>
          <TabsContent value="videos">
            <VideoGrid
              videos={horizontal}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              emptyMessage={`No videos tagged #${normalized} found on your relays.`}
            />
          </TabsContent>
          <TabsContent value="shorts">
            <VideoGrid
              videos={vertical}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              variant="shorts"
              emptyMessage={`No shorts tagged #${normalized} found on your relays.`}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
